// Searchable ward picker — Popover+Command combobox over WARD_OPTIONS.
//
// Replaces the plain <Select> the four ward fields used while the list was
// 10 entries; with the full post-2025 Hà Nội list (~126 phường/xã) a
// type-to-filter control is the only usable shape. Search is
// diacritic-insensitive ("my dinh" finds "Mỹ Đình") via deburrVi, and the
// list groups phường before xã to keep the urban units — the product's
// main market — on top.
//
// The trigger mirrors SelectTrigger's look so swapped-in call sites don't
// shift visually. `modal` on the Popover keeps the body-portaled list
// wheel-scrollable inside dialogs (same fix as the property/lead pickers).

import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  WARD_OPTIONS,
  deburrVi,
  wardLabel,
  type WardOption,
} from "@/features/property/utils/ward-options";

const CLEAR_VALUE = "__NONE__";

export interface WardComboboxProps {
  /** Selected ward code, or null when nothing is picked. */
  value: string | null;
  /** Selection callback; null = cleared (only fired when `clearable`). */
  onChange: (code: string | null) => void;
  /** Show a clear row at the top of the list. */
  clearable?: boolean | undefined;
  /** Label for the clear row (e.g. "Tất cả" in filters). */
  clearLabel?: string | undefined;
  /** Trigger placeholder while nothing is selected. */
  placeholder?: string | undefined;
  /** Forwarded to the trigger so form labels can point at it. */
  id?: string | undefined;
  ariaLabel?: string | undefined;
  disabled?: boolean | undefined;
}

export function WardCombobox({
  value,
  onChange,
  clearable,
  clearLabel = "Không chọn",
  placeholder = "Chọn khu vực",
  id,
  ariaLabel = "Khu vực",
  disabled,
}: WardComboboxProps) {
  const [open, setOpen] = useState(false);

  const groups = useMemo(() => {
    const phuong = WARD_OPTIONS.filter((w) => w.kind === "phuong");
    const xa = WARD_OPTIONS.filter((w) => w.kind === "xa");
    // Pre-2025 market-area names (Mỹ Đình, Nam Từ Liêm) — not official
    // units any more, but brokers still search by them and old listings
    // reference them.
    const khuVuc = WARD_OPTIONS.filter((w) => w.kind === "khu_vuc");
    return [
      { heading: "Phường", options: phuong },
      { heading: "Xã", options: xa },
      { heading: "Khu vực khác", options: khuVuc },
    ].filter((g) => g.options.length > 0);
  }, []);

  const pick = (code: string | null) => {
    onChange(code);
    setOpen(false);
  };

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            // Mirrors SelectTrigger (default size) so swapped call sites keep
            // their visual rhythm.
            "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 py-2 text-sm shadow-[var(--shadow-soft)] transition-[color,box-shadow] outline-none",
            "focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <span className={cn("truncate", value === null && "text-ink-faint")}>
            {value !== null ? wardLabel(value) : placeholder}
          </span>
          <ChevronDown
            aria-hidden="true"
            className="size-4 shrink-0 text-muted-foreground opacity-50"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[220px] p-0"
      >
        <Command
          filter={(itemValue, search) => {
            // itemValue is the item's haystack (deburred + raw label + code);
            // deburr the query too so accents never matter on either side.
            return itemValue.includes(deburrVi(search)) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Tìm phường, xã..." />
          <CommandList>
            <CommandEmpty>Không tìm thấy khu vực.</CommandEmpty>
            {clearable ? (
              <CommandGroup>
                <CommandItem
                  value={CLEAR_VALUE}
                  onSelect={() => pick(null)}
                  className="text-muted-foreground"
                >
                  {clearLabel}
                </CommandItem>
              </CommandGroup>
            ) : null}
            {groups.map((group) => (
              <CommandGroup key={group.heading} heading={group.heading}>
                {group.options.map((w) => (
                  <CommandItem
                    key={w.value}
                    value={haystack(w)}
                    onSelect={() => pick(w.value)}
                    data-checked={w.value === value}
                    className="data-[checked=true]:font-semibold"
                  >
                    {w.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Searchable haystack; ends with the code so every item value is unique. */
function haystack(w: WardOption): string {
  return `${deburrVi(w.label)} ${w.label.toLowerCase()} ${w.value.toLowerCase()}`;
}
